import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  approveAppointment,
  assignAppointment,
  cancelAppointment,
  checkStaffAvailability,
  fetchAppointment,
  fetchSalonStaff,
  proposeAppointmentSchedule,
  rejectAppointment,
  respondToAppointmentSchedule,
  respondToStaffProposal,
  respondToAssignment,
  sendChatMessage,
  updateAppointmentStatus,
  uploadAppointmentPaymentSlip,
  verifyAppointmentPaymentSlip,
} from '../../api/appointments';
import PaymentSlipSection from '../payments/PaymentSlipSection';
import { salonBlockedBySlip } from '../../utils/paymentSlip';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { getFriendlyError } from '../../utils/friendlyError';
import { STATUS_FLOW } from '../../utils/appointmentStatus';
import DatePicker from '../ui/DatePicker';
import TimePicker from '../ui/TimePicker';
import StaffAssignmentBadge from './StaffAssignmentBadge';
import StatusBadge from './StatusBadge';
import InvoiceActions from '../invoices/InvoiceActions';
import AppointmentPriceAdjustment, { AppointmentPricingReceipt } from '../pricing/AppointmentPriceAdjustment';

const staffRefId = (staff) => {
  if (!staff) return '';
  return staff._id?.toString?.() || String(staff);
};

const groupChatByDate = (chat) => {
  const groups = {};
  (chat || []).forEach((msg) => {
    const key = new Date(msg.sentAt).toLocaleDateString();
    if (!groups[key]) groups[key] = [];
    groups[key].push(msg);
  });
  return groups;
};

const AppointmentDetailDrawer = ({ appointmentId, onClose, clientMode }) => {
  const { t } = useTranslation();
  const { role, user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [staffId, setStaffId] = useState('');
  const [confirmedDate, setConfirmedDate] = useState('');
  const [confirmedTime, setConfirmedTime] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [assignmentRejectReason, setAssignmentRejectReason] = useState('');
  const [assignmentCancelReason, setAssignmentCancelReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const chatEndRef = useRef(null);

  const { data, refetch, isLoading, isError, error } = useQuery({
    queryKey: ['appointment', appointmentId],
    queryFn: () => fetchAppointment(appointmentId),
    enabled: !!appointmentId,
    refetchInterval: 5000,
  });

  const appointment = data?.appointment;
  const appointmentServiceId = appointment?.serviceId
    ? String(appointment.serviceId._id || appointment.serviceId)
    : '';
  const appointmentBranchId = appointment?.branchId
    ? String(appointment.branchId._id || appointment.branchId)
    : '';

  const { data: staffData, isLoading: staffLoading } = useQuery({
    queryKey: ['salon-staff', appointmentServiceId],
    queryFn: () => fetchSalonStaff({ serviceId: appointmentServiceId }),
    enabled: !clientMode && !!appointmentId && !!appointmentServiceId,
  });

  const { data: availabilityData, isFetching: checkingAvailability } = useQuery({
    queryKey: [
      'staff-availability',
      appointmentId,
      staffId,
      confirmedDate,
      confirmedTime,
      appointmentServiceId,
      appointmentBranchId,
    ],
    queryFn: () =>
      checkStaffAvailability({
        staffId,
        date: confirmedDate,
        time: confirmedTime,
        serviceId: appointmentServiceId,
        branchId: appointmentBranchId,
        excludeAppointmentId: appointmentId,
      }),
    enabled:
      !clientMode &&
      !!staffId &&
      !!confirmedDate &&
      !!confirmedTime &&
      !!appointmentServiceId &&
      !!appointmentBranchId,
  });

  const staffSlotAvailable = availabilityData?.available !== false;
  const staffSlotChecked = !!staffId && !!confirmedDate && !!confirmedTime && !checkingAvailability;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['appointments'] });
    queryClient.invalidateQueries({ queryKey: ['client-appointments'] });
    queryClient.invalidateQueries({ queryKey: ['appointment', appointmentId] });
  };

  const slipUploadMut = useMutation({
    mutationFn: ({ file, bankAccountId }) =>
      uploadAppointmentPaymentSlip(appointmentId, file, bankAccountId),
    onSuccess: () => {
      showToast(t('toast.paymentSlipUploaded'), 'success');
      invalidate();
    },
    onError: (err) => showToast(getFriendlyError(err, t('errors.uploadFailed')), 'error'),
  });

  const slipVerifyMut = useMutation({
    mutationFn: (index) => verifyAppointmentPaymentSlip(appointmentId, index),
    onSuccess: () => {
      showToast(t('toast.paymentVerified'), 'success');
      invalidate();
    },
  });

  const approveMut = useMutation({
    mutationFn: () => approveAppointment(appointmentId),
    onSuccess: () => { showToast(t('toast.approved'), 'success'); invalidate(); },
    onError: (err) => showToast(getFriendlyError(err, t('toast.errorGeneric')), 'error'),
  });

  const rejectMut = useMutation({
    mutationFn: () => rejectAppointment(appointmentId, rejectReason),
    onSuccess: () => { showToast(t('toast.rejected'), 'success'); invalidate(); },
  });

  const assignMut = useMutation({
    mutationFn: (options = {}) =>
      assignAppointment(appointmentId, {
        staffId,
        confirmedDate,
        confirmedTime,
        requestStaffSlotApproval: options.requestStaffSlotApproval,
      }),
    onSuccess: (res) => {
      showToast(
        res.awaitingStaffSlotApproval
          ? res.message || t('toast.awaitingStaffSlotApproval')
          : res.awaitingClientStaffApproval
            ? res.message || t('toast.awaitingClientStaffApproval')
            : t('toast.staffAssignmentUpdated'),
        'success'
      );
      invalidate();
    },
    onError: (err) => showToast(getFriendlyError(err, t('toast.assignmentFailed')), 'error'),
  });

  const proposeScheduleMut = useMutation({
    mutationFn: () =>
      proposeAppointmentSchedule(appointmentId, { confirmedDate, confirmedTime }),
    onSuccess: () => { showToast(t('toast.scheduleProposed'), 'success'); invalidate(); },
    onError: (err) => showToast(getFriendlyError(err, t('toast.scheduleProposalFailed')), 'error'),
  });

  const scheduleRespMut = useMutation({
    mutationFn: (body) => respondToAppointmentSchedule(appointmentId, body),
    onSuccess: (_, { action }) => {
      showToast(
        action === 'accept' ? t('toast.scheduleAccepted') : t('toast.scheduleRejected'),
        'success'
      );
      invalidate();
    },
    onError: (err) => showToast(getFriendlyError(err, t('toast.errorGeneric')), 'error'),
  });

  const staffProposalRespMut = useMutation({
    mutationFn: (body) => respondToStaffProposal(appointmentId, body),
    onSuccess: (_, { action }) => {
      showToast(
        action === 'accept' ? t('toast.staffProposalAccepted') : t('toast.staffProposalRejected'),
        'success'
      );
      invalidate();
    },
    onError: (err) => showToast(getFriendlyError(err, t('toast.errorGeneric')), 'error'),
  });

  const assignmentMut = useMutation({
    mutationFn: (body) => respondToAssignment(appointmentId, body),
    onSuccess: (_, { action }) => {
      showToast(
        action === 'accept' ? t('toast.assignmentAccepted') : t('toast.assignmentDeclined'),
        'success'
      );
      setAssignmentRejectReason('');
      setAssignmentCancelReason('');
      invalidate();
    },
    onError: (err) => showToast(getFriendlyError(err, t('toast.assignmentFailed')), 'error'),
  });

  const statusMut = useMutation({
    mutationFn: (status) => updateAppointmentStatus(appointmentId, status),
    onSuccess: () => { showToast(t('toast.statusUpdated'), 'success'); invalidate(); },
  });

  const cancelMut = useMutation({
    mutationFn: (reason) => cancelAppointment(appointmentId, reason),
    onSuccess: () => {
      showToast(t('toast.appointmentCancelled'), 'success');
      setCancelReason('');
      invalidate();
      onClose();
    },
    onError: (err) => showToast(getFriendlyError(err, t('toast.errorGeneric')), 'error'),
  });

  const submitClientCancel = () => {
    const reason = cancelReason.trim();
    if (!reason) {
      showToast(t('toast.cancelReasonRequired'), 'error');
      return;
    }
    cancelMut.mutate(reason);
  };

  const chatMut = useMutation({
    mutationFn: (text) => sendChatMessage(appointmentId, text),
    onSuccess: () => {
      setMessage('');
      refetch();
    },
  });

  useEffect(() => {
    if (activeTab !== 'chat') return;
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [appointment?.chat, activeTab]);

  useEffect(() => {
    if (!appointmentId) return;
    setActiveTab('details');
  }, [appointmentId]);

  useEffect(() => {
    if (!appointment || clientMode) return;
    const chat = appointment.chat || [];
    if (!chat.length) return;
    const last = chat[chat.length - 1];
    const myId = String(user?.id || user?._id);
    const lastSenderId = String(last.senderId?._id || last.senderId);
    if (last.senderRole === 'CLIENT' && lastSenderId !== myId) {
      setActiveTab('chat');
    }
  }, [appointment, clientMode, user?.id, user?._id]);

  useEffect(() => {
    if (!appointment) return;
    const initialStaffId =
      staffRefId(appointment.assignedStaffId) || staffRefId(appointment.preferredStaffId);
    setStaffId(initialStaffId);
    if (appointment.confirmedDate) {
      setConfirmedDate(new Date(appointment.confirmedDate).toISOString().split('T')[0]);
    } else if (appointment.preferredDate) {
      setConfirmedDate(new Date(appointment.preferredDate).toISOString().split('T')[0]);
    }
    setConfirmedTime(appointment.confirmedTime || appointment.preferredTime || '');
  }, [appointment]);

  useEffect(() => {
    if (!staffId || !staffData?.staff?.length) return;
    const eligible = staffData.staff.some((s) => String(s._id) === staffId);
    if (!eligible) setStaffId('');
  }, [staffData, staffId]);

  if (!appointmentId) return null;

  const canManage =
    ['SALON_MANAGER', 'SUPER_ADMIN'].includes(role) ||
    user?.canManageAppointments ||
    user?.permissions?.canManageAppointments;

  const paymentBlocked = appointment
    ? salonBlockedBySlip(appointment, appointment.payment?.paymentSlipPolicy)
    : false;

  const assignmentStatus = appointment?.staffAssignmentStatus;
  const isAssignedStaff =
    appointment && staffRefId(appointment.assignedStaffId) === String(user?.id || user?._id);

  const awaitingStaffSlotApproval = !!appointment?.awaitingStaffSlotApproval;
  const canAssign = canManage && appointment?.status === 'APPROVED';
  const canReassign =
    canManage && appointment?.status === 'ASSIGNED' && appointment?.status !== 'IN_PROGRESS';
  const canManageSlotApproval =
    canManage && awaitingStaffSlotApproval && appointment?.status === 'PENDING';
  const showStaffResponse =
    isAssignedStaff &&
    assignmentStatus === 'PENDING' &&
    (appointment?.status === 'ASSIGNED' || awaitingStaffSlotApproval);
  const showStaffCancel =
    isAssignedStaff && assignmentStatus === 'ACCEPTED' && appointment?.status === 'ASSIGNED';
  const canStartAssigned =
    appointment?.status === 'ASSIGNED' &&
    (assignmentStatus === 'ACCEPTED' || canManage);

  const prefDate = appointment?.preferredDate
    ? new Date(appointment.preferredDate).toISOString().split('T')[0]
    : '';
  const scheduleChanged =
    prefDate &&
    confirmedDate &&
    (confirmedDate !== prefDate || String(confirmedTime) !== String(appointment?.preferredTime || ''));
  const schedulePending = appointment?.scheduleStatus === 'PENDING';
  const scheduleAccepted = appointment?.scheduleStatus === 'ACCEPTED';
  const staffProposalPending = appointment?.staffProposalStatus === 'PENDING';

  const chatGroups = groupChatByDate(appointment?.chat);
  const chatCount = appointment?.chat?.length || 0;
  const myUserId = String(user?.id || user?._id);
  const hasClientChat =
    !clientMode &&
    (appointment?.chat || []).some(
      (msg) => msg.senderRole === 'CLIENT' && String(msg.senderId?._id || msg.senderId) !== myUserId
    );

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-xl flex-col border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 p-5">
          <h2 className="text-lg font-semibold">{t('appointments.shortTitle')}</h2>
          <button type="button" onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>

        {isLoading && !appointment && <p className="p-5 text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}
        {isError && (
          <p className="p-5 text-sm text-red-400">
            {getFriendlyError(error, t('appointments.drawer.loadError'))}
          </p>
        )}

        {appointment && (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="border-b border-zinc-200 px-5 py-3 dark:border-zinc-800">
              <div className="flex items-center gap-3">
                {appointment.clientId?.profilePicture ? (
                  <img src={appointment.clientId.profilePicture} alt="" className="h-12 w-12 rounded-full object-cover" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800">
                    {appointment.clientId?.firstName?.[0]}
                  </div>
                )}
                <div>
                  <p className="font-medium">{appointment.clientName}</p>
                  <p className="text-sm text-zinc-600 dark:text-zinc-500">{appointment.serviceName}</p>
                  <p className="text-xs text-zinc-600 dark:text-zinc-500">{appointment.branchName}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusBadge status={appointment.status} />
                  {appointment.assignedStaffId && assignmentStatus && (
                    <StaffAssignmentBadge status={assignmentStatus} />
                  )}
                </div>
              </div>
            </div>

            <div className="flex border-b border-zinc-200 dark:border-zinc-800">
              <button
                type="button"
                onClick={() => setActiveTab('details')}
                className={`flex-1 px-4 py-2.5 text-sm font-medium transition ${
                  activeTab === 'details'
                    ? 'border-b-2 border-glow-500 text-glow-600 dark:text-glow-400'
                    : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                }`}
              >
                {t('appointments.drawer.tabDetails')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('chat')}
                className={`relative flex-1 px-4 py-2.5 text-sm font-medium transition ${
                  activeTab === 'chat'
                    ? 'border-b-2 border-glow-500 text-glow-600 dark:text-glow-400'
                    : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                }`}
              >
                {t('appointments.drawer.tabChat')}
                {chatCount > 0 && (
                  <span className="ml-1.5 rounded-full bg-glow-600/20 px-1.5 py-0.5 text-xs text-glow-600 dark:text-glow-400">
                    {chatCount}
                  </span>
                )}
                {hasClientChat && activeTab !== 'chat' && (
                  <span className="absolute right-6 top-2 h-2 w-2 rounded-full bg-amber-500" />
                )}
              </button>
            </div>

            {activeTab === 'details' && (
            <div className="flex-1 space-y-4 overflow-y-auto p-5">
              {appointment.preferredStaffName && !appointment.assignedStaffName && (
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('appointments.drawer.clientPreferredStaff', { name: appointment.preferredStaffName })}
                </p>
              )}

              {appointment.assignedStaffName && (
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('appointments.drawer.assignedTo', { name: appointment.assignedStaffName })}
                </p>
              )}

              {staffProposalPending && appointment.proposedStaffName && (
                <p className="text-sm text-amber-300">
                  {t('appointments.drawer.awaitingClientStaffApproval', { name: appointment.proposedStaffName })}
                </p>
              )}

              {awaitingStaffSlotApproval && (
                <p className="text-sm text-amber-300">
                  {clientMode
                    ? t('appointments.drawer.awaitingStaffSlotApprovalClient')
                    : t('appointments.drawer.awaitingStaffSlotApprovalSalon', {
                        name: appointment.assignedStaffName || t('appointments.drawer.selectStaff'),
                      })}
                </p>
              )}

              {assignmentStatus === 'REJECTED' && appointment.staffAssignmentRejectionReason && (
                <div className="rounded-lg border border-red-800/40 bg-red-950/30 p-3 text-sm">
                  <p className="font-medium text-red-300">{t('appointments.drawer.staffDeclined')}</p>
                  <p className="mt-1 text-red-200/80">{appointment.staffAssignmentRejectionReason}</p>
                  {appointment.staffAssignmentRespondedAt && (
                    <p className="mt-2 text-xs text-red-300/60">
                      {new Date(appointment.staffAssignmentRespondedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-2 text-xs">
                {STATUS_FLOW.map((s) => (
                  <span
                    key={s}
                    className={`rounded px-2 py-0.5 ${
                      STATUS_FLOW.indexOf(s) <= STATUS_FLOW.indexOf(appointment.status)
                        ? 'bg-glow-600/30 text-glow-300'
                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-500'
                    }`}
                  >
                    {t(`status.appointment.${s}`, { defaultValue: s })}
                  </span>
                ))}
              </div>

              {appointment.status === 'CANCELLED' && appointment.cancellationReason && (
                <div className="rounded-lg border border-red-800/40 bg-red-950/30 p-3 text-sm">
                  <p className="font-medium text-red-300">
                    {clientMode
                      ? t('appointments.drawer.yourCancellationReason')
                      : t('appointments.drawer.clientCancellationReason')}
                  </p>
                  <p className="mt-1 text-red-200/80">{appointment.cancellationReason}</p>
                </div>
              )}

              {appointment.status === 'COMPLETED' && (
                <div className="space-y-3">
                  <AppointmentPricingReceipt appointment={appointment} compact={clientMode} />
                  <InvoiceActions kind="appointment" entityId={appointment._id} />
                </div>
              )}

              <PaymentSlipSection
                entity={appointment}
                requiresAdvance={appointment.payment?.requiresAdvance}
                advanceAmount={appointment.payment?.advanceAmount}
                bankAccounts={appointment.bankAccounts || []}
                salonMode={!clientMode}
                canManage={canManage}
                onUpload={(file, bankAccountId) => slipUploadMut.mutate({ file, bankAccountId })}
                onVerify={(index) => slipVerifyMut.mutate(index)}
                uploading={slipUploadMut.isPending}
              />

              {!clientMode && canManage && (
                <AppointmentPriceAdjustment appointment={appointment} onUpdated={invalidate} />
              )}

              {clientMode && appointment.status === 'PENDING' && !awaitingStaffSlotApproval && (
                <div className="space-y-2 rounded-lg border border-red-800/40 bg-red-950/20 p-3">
                  <p className="text-sm text-red-200">
                    {t('appointments.drawer.awaitingApprovalClient')}
                  </p>
                  <label className="block text-xs text-zinc-600 dark:text-zinc-400" htmlFor="client-cancel-reason">
                    {t('appointments.cancel.reasonLabel')}
                  </label>
                  <textarea
                    id="client-cancel-reason"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value.slice(0, 500))}
                    placeholder={t('appointments.drawer.cancelReasonPlaceholderShort')}
                    rows={3}
                    className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    disabled={cancelMut.isPending || !cancelReason.trim()}
                    onClick={submitClientCancel}
                    className="w-full rounded-lg border border-red-800 py-2 text-sm text-red-400 disabled:opacity-50"
                  >
                    {cancelMut.isPending ? t('appointments.cancel.cancelling') : t('appointments.drawer.cancelAppointment')}
                  </button>
                </div>
              )}

              {!clientMode && canManage && appointment.status === 'PENDING' && !awaitingStaffSlotApproval && (
                <div className="space-y-2">
                  {paymentBlocked && (
                    <p className="text-xs text-red-400">{t('payments.salonWaitingForSlip')}</p>
                  )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={paymentBlocked || approveMut.isPending}
                    onClick={() => approveMut.mutate()}
                    className="flex-1 rounded-lg bg-emerald-600 py-2 text-sm text-white disabled:opacity-50"
                  >
                    ✓ {t('appointments.drawer.approve')}
                  </button>
                  <button type="button" onClick={() => rejectMut.mutate()} className="flex-1 rounded-lg border border-red-800 py-2 text-sm text-red-400">
                    ✗ {t('appointments.drawer.reject')}
                  </button>
                  <input
                    placeholder={t('appointments.drawer.rejectReason')}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1 text-xs"
                  />
                </div>
                </div>
              )}

              {!clientMode && showStaffResponse && (
                <div className="space-y-2 rounded-lg border border-amber-800/40 bg-amber-950/20 p-3">
                  <p className="text-sm text-amber-200">
                    {awaitingStaffSlotApproval
                      ? t('appointments.drawer.slotApprovalPrompt')
                      : t('appointments.drawer.assignmentPrompt')}
                  </p>
                  <button
                    type="button"
                    disabled={assignmentMut.isPending}
                    onClick={() => assignmentMut.mutate({ action: 'accept' })}
                    className="w-full rounded-lg bg-emerald-600 py-2 text-sm text-white disabled:opacity-50"
                  >
                    {t('appointments.drawer.acceptAssignment')}
                  </button>
                  <textarea
                    value={assignmentRejectReason}
                    onChange={(e) => setAssignmentRejectReason(e.target.value)}
                    placeholder={t('appointments.drawer.assignmentRejectReason')}
                    rows={2}
                    className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    disabled={assignmentMut.isPending || !assignmentRejectReason.trim()}
                    onClick={() =>
                      assignmentMut.mutate({
                        action: 'reject',
                        reason: assignmentRejectReason.trim(),
                      })
                    }
                    className="w-full rounded-lg border border-red-800 py-2 text-sm text-red-400 disabled:opacity-50"
                  >
                    {t('appointments.drawer.declineAssignment')}
                  </button>
                </div>
              )}

              {!clientMode && showStaffCancel && (
                <div className="space-y-2 rounded-lg border border-red-800/40 bg-red-950/20 p-3">
                  <p className="text-sm text-red-200">
                    {t('appointments.drawer.assignmentCancelHint')}
                  </p>
                  <textarea
                    value={assignmentCancelReason}
                    onChange={(e) => setAssignmentCancelReason(e.target.value)}
                    placeholder={t('appointments.drawer.assignmentCancelReason')}
                    rows={2}
                    className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    disabled={assignmentMut.isPending || !assignmentCancelReason.trim()}
                    onClick={() =>
                      assignmentMut.mutate({
                        action: 'cancel',
                        reason: assignmentCancelReason.trim(),
                      })
                    }
                    className="w-full rounded-lg border border-red-800 py-2 text-sm text-red-400 disabled:opacity-50"
                  >
                    {t('appointments.drawer.cancelAssignment')}
                  </button>
                </div>
              )}

              {!clientMode && (canAssign || canReassign || canManageSlotApproval) && (
                <div className="space-y-2 rounded-lg border border-zinc-200 dark:border-zinc-800 p-3">
                  <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    {canReassign || canManageSlotApproval
                      ? t('appointments.drawer.changeAssignedStaff')
                      : t('appointments.drawer.assignStaff')}
                  </p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-500">
                    {t('appointments.drawer.staffFilteredByService', { service: appointment.serviceName })}
                  </p>
                  {schedulePending && (
                    <p className="text-xs text-amber-400">
                      {t('appointments.drawer.schedulePendingClient')}
                    </p>
                  )}
                  <select
                    value={staffId}
                    onChange={(e) => setStaffId(e.target.value)}
                    disabled={staffLoading}
                    className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-2 text-sm disabled:opacity-50"
                  >
                    <option value="">
                      {staffLoading
                        ? t('common.loading')
                        : t('appointments.drawer.selectStaffPlaceholder')}
                    </option>
                    {(staffData?.staff || []).map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.firstName} {s.lastName}
                      </option>
                    ))}
                  </select>
                  {!staffLoading && (staffData?.staff || []).length === 0 && (
                    <p className="text-xs text-amber-500">{t('appointments.drawer.noStaffForService')}</p>
                  )}
                  <DatePicker
                    id="confirmed-date"
                    label={t('appointments.drawer.confirmedDate')}
                    compact
                    value={confirmedDate}
                    onChange={(e) => setConfirmedDate(e.target.value)}
                  />
                  <TimePicker
                    id="confirmed-time"
                    label={t('appointments.drawer.confirmedTime')}
                    compact
                    value={confirmedTime}
                    onChange={(e) => setConfirmedTime(e.target.value)}
                  />
                  {staffSlotChecked && !staffSlotAvailable && (
                    <p className="text-xs text-amber-400">
                      {t('appointments.drawer.staffUnavailableHint')}
                    </p>
                  )}
                  {scheduleChanged && !scheduleAccepted ? (
                    <button
                      type="button"
                      disabled={
                        !staffId ||
                        !confirmedDate ||
                        !confirmedTime ||
                        assignMut.isPending ||
                        proposeScheduleMut.isPending ||
                        schedulePending ||
                        staffProposalPending ||
                        paymentBlocked
                      }
                      onClick={() => proposeScheduleMut.mutate()}
                      className="w-full rounded-lg bg-purple-600 py-2 text-sm text-white disabled:opacity-50"
                    >
                      {t('appointments.drawer.sendScheduleToClient')}
                    </button>
                  ) : staffSlotChecked && !staffSlotAvailable ? (
                    <button
                      type="button"
                      disabled={
                        !staffId ||
                        !confirmedDate ||
                        !confirmedTime ||
                        assignMut.isPending ||
                        schedulePending ||
                        staffProposalPending ||
                        paymentBlocked
                      }
                      onClick={() => assignMut.mutate({ requestStaffSlotApproval: true })}
                      className="w-full rounded-lg bg-amber-600 py-2 text-sm text-white disabled:opacity-50"
                    >
                      {t('appointments.drawer.askStaffSlotApproval')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={
                        !staffId ||
                        !confirmedDate ||
                        !confirmedTime ||
                        assignMut.isPending ||
                        proposeScheduleMut.isPending ||
                        schedulePending ||
                        staffProposalPending ||
                        checkingAvailability ||
                        paymentBlocked
                      }
                      onClick={() => assignMut.mutate()}
                      className="w-full rounded-lg bg-purple-600 py-2 text-sm text-white disabled:opacity-50"
                    >
                      {canReassign || canManageSlotApproval
                        ? t('appointments.drawer.updateAssignment')
                        : t('appointments.drawer.assignAndSendRequest')}
                    </button>
                  )}
                </div>
              )}

              {clientMode && appointment?.staffProposalStatus === 'PENDING' && appointment?.proposedStaffName && (
                <div className="space-y-2 rounded-lg border border-amber-800/40 bg-amber-950/20 p-3">
                  <p className="text-sm text-amber-200">
                    {t('appointments.drawer.staffProposalPrompt', {
                      proposed: appointment.proposedStaffName,
                      preferred: appointment.preferredStaffName || t('appointments.drawer.yourChoice'),
                    })}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={staffProposalRespMut.isPending}
                      onClick={() => staffProposalRespMut.mutate({ action: 'accept' })}
                      className="flex-1 rounded-lg bg-emerald-600 py-2 text-sm text-white disabled:opacity-50"
                    >
                      {t('appointments.drawer.acceptStaffChange')}
                    </button>
                    <button
                      type="button"
                      disabled={staffProposalRespMut.isPending}
                      onClick={() =>
                        staffProposalRespMut.mutate({
                          action: 'reject',
                          reason: t('appointments.drawer.staffChangeNotOk'),
                        })
                      }
                      className="flex-1 rounded-lg border border-red-800 py-2 text-sm text-red-400 disabled:opacity-50"
                    >
                      {t('appointments.drawer.rejectStaffChange')}
                    </button>
                  </div>
                </div>
              )}

              {clientMode && appointment?.status === 'APPROVED' && appointment?.scheduleStatus === 'PENDING' && (
                <div className="space-y-2 rounded-lg border border-amber-800/40 bg-amber-950/20 p-3">
                  <p className="text-sm text-amber-200">{t('appointments.drawer.scheduleConfirmPrompt')}</p>
                  <p className="text-xs text-zinc-500">
                    {new Date(appointment.confirmedDate).toLocaleDateString()} · {appointment.confirmedTime}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={scheduleRespMut.isPending}
                      onClick={() => scheduleRespMut.mutate({ action: 'accept' })}
                      className="flex-1 rounded-lg bg-emerald-600 py-2 text-sm text-white disabled:opacity-50"
                    >
                      {t('appointments.drawer.acceptSchedule')}
                    </button>
                    <button
                      type="button"
                      disabled={scheduleRespMut.isPending}
                      onClick={() => scheduleRespMut.mutate({ action: 'reject', reason: t('appointments.drawer.scheduleNotOk') })}
                      className="flex-1 rounded-lg border border-red-800 py-2 text-sm text-red-400 disabled:opacity-50"
                    >
                      {t('appointments.drawer.rejectSchedule')}
                    </button>
                  </div>
                </div>
              )}

              {!clientMode && ['ASSIGNED', 'IN_PROGRESS'].includes(appointment.status) && (
                <div className="flex flex-wrap gap-2">
                  {paymentBlocked && (
                    <p className="w-full text-xs text-red-400">{t('payments.salonWaitingForSlip')}</p>
                  )}
                  {appointment.status === 'ASSIGNED' && canStartAssigned && (
                    <button
                      type="button"
                      disabled={paymentBlocked || statusMut.isPending}
                      onClick={() => statusMut.mutate('IN_PROGRESS')}
                      className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
                    >
                      ▶ {t('appointments.drawer.start')}
                    </button>
                  )}
                  {appointment.status === 'ASSIGNED' && assignmentStatus === 'PENDING' && isAssignedStaff && (
                    <p className="w-full text-xs text-amber-400">{t('appointments.drawer.acceptBeforeStart')}</p>
                  )}
                  <button
                    type="button"
                    disabled={paymentBlocked || statusMut.isPending}
                    onClick={() => statusMut.mutate('COMPLETED')}
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
                  >
                    ✓ {t('appointments.drawer.complete')}
                  </button>
                  <button type="button" onClick={() => statusMut.mutate('CANCELLED')} className="rounded-lg border border-red-800 px-3 py-1.5 text-xs text-red-400">{t('common.cancel')}</button>
                </div>
              )}
            </div>
            )}

            {activeTab === 'chat' && (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 space-y-4 overflow-y-auto p-4">
                {!chatCount && (
                  <p className="text-center text-sm text-zinc-500">{t('appointments.drawer.noMessages')}</p>
                )}
                {Object.entries(chatGroups).map(([date, messages]) => (
                  <div key={date}>
                    <p className="mb-2 text-center text-xs text-zinc-600 dark:text-zinc-500">{date}</p>
                    {messages.map((msg) => (
                      <div
                        key={msg._id}
                        className={`mb-2 rounded-lg px-3 py-2 text-sm ${
                          String(msg.senderId?._id || msg.senderId) === myUserId
                            ? 'ml-8 bg-glow-600/20'
                            : 'mr-8 bg-zinc-100 dark:bg-zinc-800'
                        }`}
                      >
                        <p className="text-xs text-zinc-600 dark:text-zinc-500">
                          {t(`roles.${msg.senderRole}`, { defaultValue: msg.senderRole?.replace(/_/g, ' ') })} · {new Date(msg.sentAt).toLocaleTimeString()}
                        </p>
                        <p className="mt-1">{msg.message}</p>
                      </div>
                    ))}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <form
                className="flex gap-2 border-t border-zinc-200 p-4 dark:border-zinc-800"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (message.trim()) chatMut.mutate(message.trim());
                }}
              >
                <input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('appointments.drawer.messagePlaceholder')}
                  className="flex-1 rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
                />
                <button type="submit" className="rounded-lg bg-glow-600 px-4 py-2 text-sm text-white">{t('common.send')}</button>
              </form>
            </div>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

export default AppointmentDetailDrawer;
